<template>
  <div
    :id="triggerId || undefined"
    role="button"
    :tabindex="isDisabled ? -1 : 0"
    :aria-haspopup="ariaHasPopup"
    :aria-expanded="isExpanded"
    :aria-disabled="isDisabled || undefined"
    :aria-label="ariaLabel ?? undefined"
    class="custom-pill-trigger inline-flex items-center max-w-full rounded-full font-semibold whitespace-nowrap"
    :class="[
      sizeClass,
      colorClasses,
      isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer active:opacity-70',
    ]"
    @click="onClick"
    @keydown.enter.prevent="onClick"
    @keydown.space.prevent="onClick"
  >
    <i v-if="triggerIcon" :class="`ti ti-${triggerIcon}`" aria-hidden="true"></i>
    <span v-if="triggerLabel" class="font-semibold shrink-0">{{ triggerLabel }}</span>
    <span class="truncate min-w-0" :class="{ 'color-medium-dark': isPlaceholder }">{{
      triggerText
    }}</span>
    <span v-if="count !== undefined && count !== null" class="font-fixed shrink-0"
      >· {{ count }}</span
    >
    <i
      v-if="isClearable && !isDisabled"
      class="ti ti-x custom-pill-trigger-clear shrink-0"
      role="button"
      tabindex="0"
      aria-label="Clear"
      @click.stop="onClear"
      @keydown.enter.stop.prevent="onClear"
    ></i>
    <i class="ti ti-chevron-down shrink-0" aria-hidden="true"></i>
  </div>
</template>

<script lang="ts">
import type { ComponentSizeType, PillColorType } from "@/types/componentType";
import { PropType, computed, defineComponent } from "vue";

// Bespoke size map (allowed per docs/design-system/02-tokens.md § Shared Sizing Constants): the
// pill needs a gap + pill padding + font triple per tier, which no single shared ladder carries.
const PILL_SIZE_CLASSES: Record<ComponentSizeType, string> = {
  small: "gap-1.5 px-(--pill-pad-x-small) text-xxs h-(--control-height-small)",
  default: "gap-1.5 px-(--pill-pad-x-default) text-xs h-(--control-height-default)",
  large: "gap-2 px-(--pill-pad-x-large) text-s h-(--control-height-large)",
};

// Neutral (uncoloured) fill — the chroma-free "text" ramp, i.e. a grey pill. Not the `light`
// ramp: that reads as near-white and the pill loses its shape against a white card.
const NEUTRAL_COLOR = "text";

export default defineComponent({
  name: "CustomPillTrigger",
  props: {
    triggerId: { type: String, default: "" },
    // Static prefix rendered before the value — e.g. "Sort:" or a field label.
    triggerLabel: { type: String, default: null },
    // The current value (or a placeholder when nothing is selected — see isPlaceholder).
    triggerText: { type: String, default: "" },
    // Tabler icon name without the "ti-" prefix.
    triggerIcon: { type: String, default: null },
    count: { type: Number as PropType<number | null>, default: null },
    color: { type: String as PropType<PillColorType | null>, default: null },
    size: { type: String as PropType<ComponentSizeType>, default: "small" },
    isDisabled: { type: Boolean, default: false },
    isExpanded: { type: Boolean, default: false },
    isClearable: { type: Boolean, default: false },
    // Greys the value text when triggerText is a placeholder rather than a selection.
    isPlaceholder: { type: Boolean, default: false },
    ariaLabel: { type: String, default: null },
    ariaHasPopup: {
      type: String as PropType<"listbox" | "tree" | "menu" | "dialog" | "grid">,
      default: "listbox",
    },
  },
  emits: ["onClick", "onClear"],
  setup(props, { emit }) {
    const sizeClass = computed(() => PILL_SIZE_CLASSES[props.size] ?? PILL_SIZE_CLASSES.small);

    const colorClasses = computed<Array<string>>(() => {
      const color = props.color ?? NEUTRAL_COLOR;
      return [`bg-color-${color}`, `color-${color}-dark`, `border-color-${color}-light`];
    });

    function onClick(): void {
      if (props.isDisabled) {
        return;
      }
      emit("onClick");
    }

    function onClear(): void {
      emit("onClear");
    }

    return { sizeClass, colorClasses, onClick, onClear };
  },
});
</script>

<style lang="scss" scoped>
.custom-pill-trigger-clear {
  cursor: pointer;
  &:hover {
    color: var(--color-danger);
  }
}
</style>
